import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, money, BILLING_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Printer, Truck } from "lucide-react";
import { useFileViewer } from "@/components/FileViewer";
import DictateButton from "@/components/DictateButton";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";

const STATUS_STYLE = {
  "Pending pickup": "bg-slate-200 text-slate-800", "In transit": "bg-sky-700 text-white",
  Requested: "bg-amber-100 text-amber-900",
  "Received by lab": "bg-teal-700 text-white", "In progress": "bg-amber-700 text-white",
  "Results received": "bg-green-800 text-white", Completed: "bg-[#134e4a] text-white",
  Cancelled: "bg-red-800 text-white",
};

const SENDOUT_STATUSES = ["Requested", "Pending pickup", "In transit", "Received by lab", "In progress", "Results received", "Completed", "Cancelled"];

const EMPTY = {
  case_id: "", destination_lab: "", destination_address: "", reason: "",
  sendout_status: "Pending pickup", test_requested: "", cpt_codes: "", materials_sent: "",
  authorized_by_id: "", sent_by_id: "", tracking_number: "", carrier: "", airbill_number: "",
  charged_to_patient: false, charge_amount: 0, billing_status: "Not Billed", billing_notes: "",
  results_received: false, results_summary: "", materials_returned: false, notes: "",
};

const badge = (status) => {
  const map = {
    "Not Billed": "bg-slate-200 text-slate-800", Billed: "bg-sky-700 text-white",
    Paid: "bg-green-800 text-white", Denied: "bg-red-800 text-white", "Write-Off": "bg-amber-700 text-white",
  };
  return map[status] || "bg-slate-200 text-slate-800";
};

export default function SendOuts() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [lab, setLab] = useState(null);
  const [filters, setFilters] = useState({ q: "", billing_status: "", pending: "", sendout_status: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { openFile, viewerElement } = useFileViewer();

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get("/sendouts", { params: filters });
      setRows(data);
    } catch (e) { if (!silent) toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(() => load(true), 5000);
  useEffect(() => {
    api.get("/cases", { params: { limit: 500 } }).then((r) => setCases(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/lab").then((r) => setLab(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      case_id: row.case_id, destination_lab: row.destination_lab || "",
      destination_address: row.destination_address || "", reason: row.reason || "",
      sendout_status: row.sendout_status || "Pending pickup",
      test_requested: row.test_requested || "",
      cpt_codes: row.cpt_codes || "",
      materials_sent: row.materials_sent || "", authorized_by_id: row.authorized_by_id || "", sent_by_id: row.sent_by_id || "",
      tracking_number: row.tracking_number || "", carrier: row.carrier || "",
      airbill_number: row.airbill_number || "", charged_to_patient: !!row.charged_to_patient,
      charge_amount: row.charge_amount || 0, billing_status: row.billing_status || "Not Billed",
      billing_notes: row.billing_notes || "", results_received: !!row.results_received,
      results_summary: row.results_summary || "", materials_returned: !!row.materials_returned, notes: row.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, charge_amount: Number(form.charge_amount) || 0 };
    if (!payload.authorized_by_id) delete payload.authorized_by_id;
    if (!payload.sent_by_id) delete payload.sent_by_id;
    try {
      if (editing) {
        delete payload.case_id;
        await api.put(`/sendouts/${editing}`, payload);
        toast.success("Send-out updated");
      } else {
        await api.post("/sendouts", payload);
        toast.success("Send-out logged");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const quickFlag = async (row, field) => {
    try {
      await api.put(`/sendouts/${row.id}`, { [field]: !row[field] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const printSlip = (row) => openFile(`/sendouts/${row.id}/packing-slip.pdf`, {
    filename: `PackingSlip_${row.accession_number}.pdf`,
    title: `Packing slip & chain of custody — ${row.accession_number}`,
  });

  const printCourierRun = () => openFile("/sendouts/packing-slips.pdf", {
    filename: "PathQity_Courier_Run.pdf",
    title: "Courier run — all send-outs awaiting pickup",
  });

  return (
    <div className="space-y-5" data-testid="sendouts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Send-Outs & Billing</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            {rows.length} send-out(s) · where it went, who authorized it, what was charged, what came back.
            <LiveDot lastSync={lastSync} testid="sendouts-live-indicator" />
          </p>
        </div>
        {can("sendouts:write") && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={printCourierRun} data-testid="print-courier-run-button"
                    className="h-11 border-[#134e4a] text-[#134e4a] hover:bg-teal-50">
              <Truck size={16} className="mr-2" /> Print courier run
            </Button>
            <Button onClick={openNew} data-testid="new-sendout-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Plus size={16} className="mr-2" /> Log send-out
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-2 border border-slate-200 bg-slate-50 p-3 md:grid-cols-4" data-testid="sendout-filters">
        <div className="relative md:col-span-2">
          <Search size={15} className="absolute left-3 top-3.5 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Accession, patient, lab, test, CPT, tracking #, slip #…" data-testid="sendout-search-input"
            className="h-11 border-slate-300 bg-white pl-9" />
        </div>
        <select value={filters.billing_status} onChange={(e) => setFilters((f) => ({ ...f, billing_status: e.target.value }))} data-testid="filter-billing-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any billing status</option>{BILLING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.pending} onChange={(e) => setFilters((f) => ({ ...f, pending: e.target.value }))} data-testid="filter-pending-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Everything</option>
          <option value="results">Awaiting results</option>
          <option value="materials">Materials not returned</option>
          <option value="billing">Not billed</option>
        </select>
      </div>

      <div className="grid gap-2 border border-slate-200 bg-white p-3 md:grid-cols-4" data-testid="sendout-status-filters">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 md:col-span-4">
          Send-out status
        </div>
        <select value={filters.sendout_status} onChange={(e) => setFilters((f) => ({ ...f, sendout_status: e.target.value }))} data-testid="filter-sendout-status-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any send-out status</option>
          {SENDOUT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[1500px] text-sm" data-testid="sendouts-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">Case</th><th className="px-3 py-2">Slip #</th><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Destination lab</th>
              <th className="px-3 py-2">Address</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Send-out status</th>
              <th className="px-3 py-2">Test requested</th><th className="px-3 py-2">CPT code(s)</th><th className="px-3 py-2">Materials sent</th>
              <th className="px-3 py-2">Authorized by</th><th className="px-3 py-2">Sent by</th><th className="px-3 py-2">Sent</th>
              <th className="px-3 py-2">Billing</th><th className="px-3 py-2 text-right">Charge</th>
              <th className="px-3 py-2">Patient charged</th><th className="px-3 py-2">Results</th>
              <th className="px-3 py-2">Materials back</th><th className="px-3 py-2 text-right">Edit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`sendout-row-${r.id}`}>
                <td className="px-3 py-2"><Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">{r.accession_number}</Link></td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700" data-testid={`sendout-slip-${r.id}`}>
                  {r.slip_number || "—"}
                </td>
                <td className="px-3 py-2">{r.patient_name}</td>
                <td className="px-3 py-2 font-medium text-slate-800">{r.destination_lab}</td>
                <td className="px-3 py-2 text-slate-600">{r.destination_address || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{r.reason || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.sendout_status] || "bg-slate-200 text-slate-800"}`} data-testid={`sendout-status-${r.id}`}>
                    {r.sendout_status || "Pending pickup"}
                  </span>
                </td>
                <td className="px-3 py-2">{r.test_requested}</td>
                <td className="px-3 py-2 font-mono text-slate-700" data-testid={`sendout-cpt-${r.id}`}>{r.cpt_codes || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{r.materials_sent || "—"}</td>
                <td className="px-3 py-2">{r.authorized_by_name || "—"}</td>
                <td className="px-3 py-2">{r.sent_by_name || "—"}</td>
                <td className="px-3 py-2 num text-slate-600">{fmtDate(r.date_sent)}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[11px] font-semibold ${badge(r.billing_status)}`}>{r.billing_status}</span></td>
                <td className="px-3 py-2 num text-right">{money(r.charge_amount)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => can("sendouts:write") && quickFlag(r, "charged_to_patient")} data-testid={`toggle-charged-${r.id}`}
                    className={r.charged_to_patient ? "font-semibold text-green-800" : "text-slate-500"}>
                    {r.charged_to_patient ? "Yes" : "No"}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => can("sendouts:write") && quickFlag(r, "results_received")} data-testid={`toggle-results-${r.id}`}
                    className={r.results_received ? "font-semibold text-green-800" : "text-amber-700"}>
                    {r.results_received ? `Back ${fmtDate(r.results_date)}` : "Pending"}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => can("sendouts:write") && quickFlag(r, "materials_returned")} data-testid={`toggle-materials-${r.id}`}
                    className={r.materials_returned ? "font-semibold text-green-800" : "text-amber-700"}>
                    {r.materials_returned ? `Returned ${fmtDate(r.materials_returned_date)}` : "Outstanding"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  {can("sendouts:write") && (
                    <span className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => printSlip(r)} data-testid={`print-slip-${r.id}`}
                              title="Packing slip & chain of custody">
                        <Printer size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-sendout-${r.id}`}>Edit</Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={19} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="sendouts-empty">
                No send-outs logged yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Update send-out" : "Log a send-out"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="sendout-form">
            {!editing && (
              <Field label="Case" required>
                <select required value={form.case_id} onChange={set("case_id")} data-testid="sendout-case-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Select case…</option>
                  {cases.map((c) => <option key={c.id} value={c.id}>{c.accession_number} — {c.patient_name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Destination lab" required>
              <Input required value={form.destination_lab} onChange={set("destination_lab")} data-testid="sendout-destination-input" className="h-11 border-slate-300" />
            </Field>
            <Field label="Destination address">
              <Input value={form.destination_address} onChange={set("destination_address")} data-testid="sendout-address-input" className="h-11 border-slate-300" placeholder="Street, city, state, ZIP" />
            </Field>
            <Field label="Send-out status">
              <select value={form.sendout_status} onChange={set("sendout_status")} data-testid="sendout-status-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {SENDOUT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Reason for send-out">
              <Input value={form.reason} onChange={set("reason")} data-testid="sendout-reason-input" className="h-11 border-slate-300" placeholder="e.g. Expert consult, test not offered in-house" />
            </Field>
            <Field label="Test requested" required>
              <Input required value={form.test_requested} onChange={set("test_requested")} data-testid="sendout-test-input" className="h-11 border-slate-300" />
            </Field>
            <Field label="CPT code(s)">
              <input
                list="cpt-codes" value={form.cpt_codes} onChange={set("cpt_codes")}
                data-testid="sendout-cpt-input" placeholder="e.g. 88342, 88341, 81210"
                className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 font-mono text-sm"
              />
              <datalist id="cpt-codes">
                {(lab?.cpt_suggestions || []).map((k) => <option key={k} value={k.split(" — ")[0]} label={k} />)}
              </datalist>
              <div className="mt-1 text-xs text-slate-500">Separate multiple codes with commas. Suggestions appear as you type.</div>
            </Field>
            <Field label="Tracking number">
              <Input value={form.tracking_number} onChange={set("tracking_number")} data-testid="sendout-tracking-input" className="h-11 border-slate-300" />
            </Field>
            <Field label="Carrier / courier">
              <Input value={form.carrier} onChange={set("carrier")} data-testid="sendout-carrier-input" className="h-11 border-slate-300" placeholder="FedEx, UPS, in-house courier…" />
            </Field>
            <Field label="Airbill / waybill number">
              <Input value={form.airbill_number} onChange={set("airbill_number")} data-testid="sendout-airbill-input" className="h-11 border-slate-300" placeholder="Prints pre-filled; leave blank for a hand-written line" />
            </Field>
            <Field label="Authorized by">
              <select value={form.authorized_by_id} onChange={set("authorized_by_id")} data-testid="sendout-authorized-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </Field>
            <Field label="Sent by (tech)">
              <select value={form.sent_by_id} onChange={set("sent_by_id")} data-testid="sendout-sentby-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Me</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </Field>
            <Field label="Billing status">
              <select value={form.billing_status} onChange={set("billing_status")} data-testid="sendout-billing-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {BILLING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Charge amount (USD)">
              <Input type="number" step="0.01" value={form.charge_amount} onChange={set("charge_amount")} data-testid="sendout-charge-input" className="h-11 border-slate-300" />
            </Field>
            <div className="sm:col-span-2 grid gap-2 border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3">
              {[
                ["charged_to_patient", "Charged to patient account", "sendout-charged-checkbox"],
                ["results_received", "Results received", "sendout-results-checkbox"],
                ["materials_returned", "Materials returned", "sendout-materials-checkbox"],
              ].map(([key, label, tid]) => (
                <label key={key} className="flex min-h-[44px] items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" checked={form[key]} onChange={set(key)} data-testid={tid} className="h-4 w-4 accent-[#134e4a]" />
                  {label}
                </label>
              ))}
            </div>
            <div className="sm:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Materials sent</Label>
                <DictateButton value={form.materials_sent}
                               onChange={(t) => setForm((f) => ({ ...f, materials_sent: t }))}
                               field="sendout_note" caseId={form.case_id} testid="dictate-materials-sent" />
              </div>
              <Textarea value={form.materials_sent} onChange={set("materials_sent")} data-testid="sendout-materials-input" className="mt-1 min-h-[90px] border-slate-300" placeholder="e.g. 2 H&E slides, block S2026-00012-B1" />
            </div>
            <div className="sm:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Results summary</Label>
                <DictateButton value={form.results_summary}
                               onChange={(t) => setForm((f) => ({ ...f, results_summary: t }))}
                               field="sendout_note" caseId={form.case_id} testid="dictate-results-summary" />
              </div>
              <Textarea value={form.results_summary} onChange={set("results_summary")} data-testid="sendout-results-summary-input" className="mt-1 min-h-[90px] border-slate-300" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Billing notes">
                <Textarea value={form.billing_notes} onChange={set("billing_notes")} data-testid="sendout-billing-notes-input" className="min-h-[80px] border-slate-300" />
              </Field>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="sendout-cancel-button" className="h-11">Cancel</Button>
              <Button type="submit" data-testid="sendout-submit-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                {editing ? "Save changes" : "Log send-out"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {viewerElement}
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
      {label}{required && <span className="text-red-800"> *</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);
